import React, { useEffect, useState } from "react";
import { Layers } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import GenerateButton from "@/components/GenerateButton";
import PromptBox from "@/components/PromptBox";
import { api, errMsg, mediaUrl } from "@/lib/api";

// AlphaGen inside Clean Plate: uses the source video and drops the matte into the Luma Mask slot when done.
export default function AlphaGenDialog({ open, onOpenChange, projectId, source, info, onStarted }) {
  const [prompt, setPrompt] = useState("");
  const [usd, setUsd] = useState(null);
  const [busy, setBusy] = useState(false);
  const secs = info?.frames && info?.fps ? info.frames / info.fps : info?.duration;
  useEffect(() => {
    if (!open || !secs) return;
    api.post("/bfx/estimate", { tool: "bfx-alphagen", kind: "video", video_seconds: secs }).then((r) => setUsd(r.data.usd)).catch(() => setUsd(null));
  }, [open, secs]);

  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bfx/generate", { project_id: projectId, tool: "bfx-alphagen", reference_id: source.id, prompt: prompt.trim() });
      await onStarted(data);
      toast.success("AlphaGen is running — the mask will attach to Luma Mask automatically");
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e, "Could not start AlphaGen")); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bfx bg-[#070B14] border-[#1065c0]/50 text-zinc-100 max-w-xl" data-testid="cp-alphagen-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl flex items-center gap-2"><Layers className="w-5 h-5 text-[#41a7f5]" />AlphaGen mask</DialogTitle></DialogHeader>
        <div className="flex gap-3 items-center">
          <video src={mediaUrl(info?.proxy_url)} muted playsInline preload="metadata" className="h-20 w-36 object-cover rounded-md bg-black border border-zinc-800" data-testid="cp-alphagen-source" />
          <div className="text-xs text-zinc-400">
            <div className="text-white font-medium truncate max-w-[260px]">{source?.name}</div>
            <div className="font-mono mt-0.5" data-testid="cp-alphagen-frames">{info?.frames} frames · {info?.fps} fps</div>
            <div className="mt-1">Source video is attached automatically.</div>
          </div>
        </div>
        <div>
          <div className="text-xs text-zinc-300 mb-1.5">What should the mask keep? <span className="text-zinc-500">(optional)</span></div>
          <PromptBox value={prompt} onChange={setPrompt} testid="cp-alphagen-prompt" minRows={2} placeholder="e.g. the man in the red shirt" className="ax-input bfx-input" />
        </div>
        <GenerateButton usd={usd} onClick={go} busy={busy} disabledReason={!source?.id ? "No source video" : ""} testid="cp-alphagen-generate" confirm className="w-full bfx-generate" />
        <p className="text-[11px] text-zinc-500 -mt-2">Runs in the background — keep working or switch sections; the matte lands in Luma Mask when it's ready.</p>
      </DialogContent>
    </Dialog>
  );
}
